// DeeperTweaksModal.js — per-flavor "Deeper tweaks" modal.
// Presentational / prop-driven (like ServiceRow): all state & math arrive via
// props; clicks call callbacks. A 540px centered panel over a dimmed backdrop.
import React from 'react';
import { COV } from './serviceFlavorsModel';

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');

const DAYS_HINT = {
  1: 'One weekly route visit.',
  2: 'Two visits a week — always looks fresh.',
  3: 'Three visits a week for high-show sites.',
};

// Uppercase muted section label.
function SectionLabel({ children, right }) {
  return (
    <div
      className="sf-dtm-section-label"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase',
        color: 'var(--sf-muted)', fontWeight: 700, marginBottom: 8,
      }}
    >
      <span>{children}</span>
      {right != null ? (
        <span className="sf-dtm-section-right num" style={{ color: 'var(--sf-ink-2)' }}>{right}</span>
      ) : null}
    </div>
  );
}

// One segmented control made of `options` ({ label, active, onClick }).
function Segmented({ options }) {
  return (
    <div className="sf-dtm-seg" style={{ display: 'flex', gap: 6 }}>
      {options.map((opt) => (
        <button
          key={opt.label}
          type="button"
          className={`sf-dtm-seg-btn${opt.active ? ' is-active' : ''}`}
          onClick={opt.onClick}
          style={{
            flex: 1, minWidth: 0, padding: '7px 8px', borderRadius: 9, cursor: 'pointer',
            fontSize: 12.5, fontWeight: 600, lineHeight: 1.2,
            border: opt.active ? '1px solid var(--sf-green)' : '1px solid var(--sf-line-2)',
            background: opt.active ? 'var(--sf-green)' : 'var(--sf-surface)',
            color: opt.active ? '#fff' : 'var(--sf-muted)',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Small −/+ stepper pair around a value.
function Stepper({ value, onInc, onDec }) {
  const btn = {
    width: 24, height: 24, lineHeight: '22px', textAlign: 'center', padding: 0,
    border: '1px solid var(--sf-line-2)', borderRadius: 6,
    background: 'var(--sf-surface)', color: 'var(--sf-ink-3)', cursor: 'pointer', fontSize: 15,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button type="button" className="sf-dtm-step" aria-label="decrease" onClick={onDec} style={btn}>−</button>
      <span className="sf-dtm-step-val num" style={{ minWidth: 48, textAlign: 'center',
        fontSize: 13.5, fontWeight: 600, color: 'var(--sf-ink-2)' }}>{value}</span>
      <button type="button" className="sf-dtm-step" aria-label="increase" onClick={onInc} style={btn}>+</button>
    </div>
  );
}

const sectionStyle = {
  padding: '16px 18px',
  borderBottom: '1px solid var(--sf-line)',
};

const rowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
};

const hintStyle = { fontSize: 11.5, color: 'var(--sf-muted-2)', marginTop: 6 };

export default function DeeperTweaksModal({
  flavor, startupStr, startupCatalog,
  onClose, onSetDays, onStepBlow, onSetPrune, onSetCov, onToggleStartup, onStepStartup,
}) {
  const svc = flavor.svc || {};
  const maint = svc.Maint || {};
  const irr = svc.Irrigation || {};
  const days = Number(maint.days) || 1;
  const blowGo = Number(maint.blowGo) || 0;
  const natural = maint.prune === 'natural';
  const cov = Math.max(0, Math.min(Number(irr.cov) || 0, COV.length - 1));
  const catalog = startupCatalog || [];

  return (
    <div
      className="sf-dtm-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(35,41,31,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40,
      }}
    >
      <div
        className="sf-dtm-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: 540, maxWidth: '100%', maxHeight: '88vh', overflow: 'auto',
          background: 'var(--sf-card)', borderRadius: 16,
          border: '1px solid var(--sf-line-2)', boxShadow: '0 20px 60px rgba(0,0,0,.35)',
        }}
      >
        {/* Sticky header */}
        <div
          className="sf-dtm-header"
          style={{
            position: 'sticky', top: 0, zIndex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', background: 'var(--sf-card)',
            borderBottom: '1px solid var(--sf-line)',
          }}
        >
          <div className="sf-dtm-title" style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-ink)' }}>
            {flavor.name} · deeper tweaks
          </div>
          <button
            type="button"
            className="sf-dtm-close"
            aria-label="Close"
            onClick={onClose}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 22, lineHeight: 1, color: 'var(--sf-muted)', padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Section 1 — Site schedule & drive time */}
        <div className="sf-dtm-section" style={sectionStyle}>
          <SectionLabel>Site schedule &amp; drive time</SectionLabel>

          <div className="sf-dtm-field" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sf-ink-2)', marginBottom: 6 }}>
              Maintenance days / week
            </div>
            <Segmented
              options={[1, 2, 3].map((n) => ({
                label: `${n} ${n === 1 ? 'day' : 'days'} / wk`,
                active: days === n,
                onClick: () => onSetDays(n),
              }))}
            />
            <div className="sf-dtm-hint" style={hintStyle}>{DAYS_HINT[days] || DAYS_HINT[1]}</div>
          </div>

          <div className="sf-dtm-field" style={{ ...rowStyle, marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sf-ink-2)' }}>
              Additional blow &amp; go visits / yr
            </div>
            <Stepper value={blowGo} onInc={() => onStepBlow(1)} onDec={() => onStepBlow(-1)} />
          </div>
          {/* Trips/drive readout removed this pass (drive-time deferred). */}
        </div>

        {/* Section 2 — Maintenance · trim style */}
        <div className="sf-dtm-section" style={sectionStyle}>
          <SectionLabel>Maintenance · trim style</SectionLabel>
          <Segmented
            options={[
              { label: 'Shearing', active: !natural, onClick: () => onSetPrune('shear') },
              { label: 'Natural pruning', active: natural, onClick: () => onSetPrune('natural') },
            ]}
          />
          {natural ? (
            <div className="sf-dtm-hint" style={hintStyle}>
              Letting shrubs grow longer between visits — trims maintenance labor ~12%.
            </div>
          ) : null}
        </div>

        {/* Section 3 — Irrigation · repair coverage */}
        <div className="sf-dtm-section" style={sectionStyle}>
          <SectionLabel>Irrigation · repair coverage</SectionLabel>
          {irr.mode === 'off' ? (
            <div className="sf-dtm-muted" style={{ fontSize: 12, color: 'var(--sf-muted)' }}>
              Irrigation is turned off for this flavor — turn it on to set repair coverage.
            </div>
          ) : (
            <>
              <Segmented
                options={COV.map((c, i) => ({
                  label: c.label,
                  active: cov === i,
                  onClick: () => onSetCov(i),
                }))}
              />
              <div className="sf-dtm-hint" style={hintStyle}>{COV[cov].note}</div>
            </>
          )}
        </div>

        {/* Section 4 — Start-up · one-time */}
        <div className="sf-dtm-section" style={{ ...sectionStyle, borderBottom: 'none' }}>
          <SectionLabel right={startupStr}>Start-up · one-time</SectionLabel>
          <div className="sf-dtm-startup-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {catalog.map((item) => {
              const cur = (flavor.startup || {})[item.key] || {};
              const on = !!cur.on;
              return (
                <div
                  key={item.key}
                  className={`sf-dtm-startup-row${on ? '' : ' is-off'}`}
                  style={{
                    ...rowStyle,
                    padding: '8px 10px', borderRadius: 10,
                    border: '1px solid var(--sf-line)', background: 'var(--sf-surface)',
                    opacity: on ? 1 : 0.55,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <button
                      type="button"
                      className="sf-dtm-startup-pill"
                      onClick={() => onToggleStartup(item.key)}
                      style={{
                        border: 'none', cursor: 'pointer', borderRadius: 999,
                        padding: '3px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.02em',
                        color: on ? '#fff' : 'var(--sf-muted)',
                        background: on ? 'var(--sf-green)' : 'var(--sf-inset-2)',
                      }}
                    >
                      {on ? 'On' : 'Off'}
                    </button>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sf-ink-2)' }}>
                      {item.label}
                    </span>
                  </div>
                  <Stepper
                    value={money(cur.cost)}
                    onInc={() => onStepStartup(item.key, 1)}
                    onDec={() => onStepStartup(item.key, -1)}
                  />
                </div>
              );
            })}
          </div>
          <div className="sf-dtm-footnote" style={{ fontSize: 11, color: 'var(--sf-muted-2)', marginTop: 10 }}>
            Billed once at start of contract, marked up at the global margin. Amounts are raw cost.
          </div>
        </div>
      </div>
    </div>
  );
}
